import { Dialog, makeStyles, Typography } from "@material-ui/core"
import clsx from "clsx"
import React, { FC, useState } from "react"
import { useApi } from "../../utils/API"
import { colors } from "../../utils/theme"
import { GhostButton, PrimaryButton } from "../atoms/button/Button"
import CloseIcon from "../atoms/CloseIcon"
import Input from "../atoms/input/Input"
import PasswordRules from "./PasswordRules"

const useStyles = makeStyles((theme) => ({
  dialogPaper: {
    border: `1px solid ${colors.neutral.clay}`,
    boxShadow: "none",
    overflow: "visible",
  },
  dialogCloseContainer: {
    display: "flex",
    alignItems: "center",
    justifyContent: "flex-end",
    marginBottom: theme.spacing(1),
  },
  dialogContent: {
    padding: theme.spacing(2),
    paddingBottom: theme.spacing(5),
  },
  closeButton: {
    margin: 0,
    padding: 0,
    border: 0,
    height: theme.spacing(2),
    width: theme.spacing(2),
    backgroundColor: "transparent",
    cursor: "pointer",
    "&:focus": {
      outline: "none",
    },
  },
  dialogTitle: {
    color: colors.neutral.denim,
    marginBottom: theme.spacing(3),
  },
  dialogSubtitle: {
    color: colors.neutral.denim,
    marginBottom: theme.spacing(3),
  },
  dialogButton: {
    marginBottom: theme.spacing(2),
    width: "100%",
  },
  cancelContainer: {
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
  },
  marginBottom24: {
    marginBottom: theme.spacing(3),
  },
  input: {
    width: "100%",
  },
  inputContainer: {
    position: "relative",
    width: "100%",
  },
}))

interface Props {
  open: boolean
  onClose: () => void
  email: string
}

const ChangePasswordDialog: FC<Props> = ({ open, onClose, email }) => {
  const classes = useStyles()
  const { changePassword } = useApi()
  const [oldPassword, setOldPassword] = useState<string>("")
  const [newPassword, setNewPassword] = useState<string>("")
  const [reenterPassword, setRenterPassword] = useState<string>("")
  const [oldPasswordError, setOldPasswordError] = useState<boolean>(false)
  const [newPasswordError, setNewPasswordError] = useState<boolean>(false)
  const [reenterPasswordError, setReenterPasswordError] =
    useState<boolean>(false)
  const [newPasswordFocus, setNewPasswordFocus] = useState<boolean>(false)
  const [reenterPasswordFocus, setReenterPasswordFocus] =
    useState<boolean>(false)
  const [validPassword, setValidPassword] = useState<boolean>(false)

  return (
    <Dialog
      fullWidth={true}
      maxWidth="xs"
      open={open}
      onClose={onClose}
      classes={{
        paper: classes.dialogPaper,
      }}
    >
      <div className={classes.dialogContent}>
        <div className={classes.dialogCloseContainer}>
          <button className={classes.closeButton} onClick={onClose}>
            <CloseIcon />
          </button>
        </div>
        <Typography variant="h2" component="h1" className={classes.dialogTitle}>
          Change Password
        </Typography>
        <Input
          value={oldPassword}
          onChange={(event) => setOldPassword(event.target.value)}
          error={oldPasswordError}
          className={clsx(classes.marginBottom24, classes.input)}
          label="Old Password"
          placeholder="Old Password"
          type="password"
          helperText={
            oldPasswordError
              ? "You must enter your current password correctly in order to continue."
              : undefined
          }
        />
        <div className={classes.inputContainer}>
          <Input
            onFocus={() => setNewPasswordFocus(true)}
            onBlur={() => setNewPasswordFocus(false)}
            focused={newPasswordFocus}
            value={newPassword}
            onChange={(event) => setNewPassword(event.target.value)}
            error={newPasswordError}
            className={clsx(classes.marginBottom24, classes.input)}
            label="New Password"
            placeholder="New Password"
            type="password"
            enableShowPassword={true}
          />
          {newPasswordFocus && (
            <PasswordRules
              password={newPassword}
              comparisonPassword={reenterPassword}
              setValidPassword={setValidPassword}
              left={426}
            />
          )}
        </div>
        <div className={classes.inputContainer}>
          <Input
            onFocus={() => setReenterPasswordFocus(true)}
            onBlur={() => setReenterPasswordFocus(false)}
            focused={reenterPasswordFocus}
            value={reenterPassword}
            onChange={(event) => setRenterPassword(event.target.value)}
            error={reenterPasswordError}
            className={clsx(classes.marginBottom24, classes.input)}
            label="Re-enter New Password"
            placeholder="Re-enter New Password"
            type="password"
            enableShowPassword={true}
          />
          {reenterPasswordFocus && (
            <PasswordRules
              password={newPassword}
              comparisonPassword={reenterPassword}
              setValidPassword={setValidPassword}
              left={426}
            />
          )}
        </div>
        <PrimaryButton
          onClick={async () => {
            setOldPasswordError(false)
            setNewPasswordError(false)
            setReenterPasswordError(false)
            if (!oldPassword) {
              setOldPasswordError(true)
              return
            }
            if (!newPassword) {
              setNewPasswordError(true)
              return
            }
            if (!reenterPassword) {
              setReenterPasswordError(true)
              return
            }
            if (!validPassword) {
              setNewPasswordError(true)
              setReenterPasswordError(true)
              return
            }
            const response = await changePassword(
              oldPassword,
              newPassword,
              email
            )
            if (
              response?.data.error &&
              response?.data.message === "Incorrect current password."
            ) {
              setOldPasswordError(true)
              return
            }
            onClose()
          }}
          className={classes.dialogButton}
          type="submit"
        >
          Save
        </PrimaryButton>
        <div className={classes.cancelContainer}>
          <GhostButton onClick={onClose}>Cancel</GhostButton>
        </div>
      </div>
    </Dialog>
  )
}

export default ChangePasswordDialog
